import React from 'react';
import { T } from '../../designTokens';
import { StatusBadge } from '../Badges';
import { formatDate } from '../../utils/date';
import { reportPath, scoreBadge } from './ReportsTable';
import type { ReportSummary } from '@privisy/api-types';

interface ReportCardsProps {
  reports: ReportSummary[];
  onView: (id: string) => void;
  onShare: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}

/** Mobile layout (≤768px): the same rows as cards, without the admin columns. */
export default function ReportCards({ reports, onView, onShare, onDelete }: ReportCardsProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {reports.map((report) => {
        const path = reportPath(report.url);
        const grade = scoreBadge(report);
        return (
          <div
            key={report.id}
            style={{
              padding: '16px',
              backgroundColor: T.gray50,
              borderRadius: `${T.radiusXl}px`,
              border: `1px solid ${T.gray200}`,
            }}
          >
            <div style={{ fontWeight: 700, color: T.gray800, fontSize: '15px', marginBottom: '2px' }}>
              {report.name || report.domain}
            </div>
            {path && (
              <div style={{ fontSize: '12px', color: T.gray400, marginBottom: '10px', fontFamily: T.fontMono, wordBreak: 'break-all' }}>
                {path}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  borderRadius: `${T.radiusFull}px`,
                  backgroundColor: grade.bg,
                  color: grade.color,
                  fontFamily: T.fontMono,
                  fontWeight: 700,
                  fontSize: '13px',
                }}
              >
                {grade.label}
              </span>
              <StatusBadge status={report.overallStatus} />
              <span style={{ color: T.gray500, fontSize: '13px' }}>{formatDate(report.createdAt)}</span>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => onView(report.id)}
                style={{
                  flex: 2,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: T.white,
                  backgroundColor: T.cobalt600,
                  border: 'none',
                  borderRadius: `${T.radiusMd}px`,
                  cursor: 'pointer',
                  minHeight: '44px',
                }}
              >
                View
              </button>
              <button
                onClick={() => onShare(report.id, report.url ?? report.id)}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: T.gray600,
                  backgroundColor: T.white,
                  border: `1px solid ${T.gray300}`,
                  borderRadius: `${T.radiusMd}px`,
                  cursor: 'pointer',
                  minHeight: '44px',
                }}
              >
                Share
              </button>
              <button
                onClick={() => onDelete(report.id)}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: T.error,
                  backgroundColor: T.white,
                  border: `1px solid ${T.errorBorder}`,
                  borderRadius: `${T.radiusMd}px`,
                  cursor: 'pointer',
                  minHeight: '44px',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
